import React, { useEffect } from "react";
import {
  Comments,
  ContentBox,
  InfoBox,
  ShortlistedTable,
} from "../../components";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  getStudentApplication,
  getStudentDetails,
} from "../../services/admin.services";
import { useDispatch, useSelector } from "react-redux";
import { setUserDetails } from "../../state/reducers/userSlice";
import {
  IconBook,
  IconFileText,
  IconMail,
  IconPhone,
  IconSchool,
} from "@tabler/icons-react";

const StudentApplicationProcess = () => {
  const isTableFullScreen = useSelector(
    (state) => state.application.isTableFullScreen
  );
  const studentDetails = useSelector((state) => state.user.userDetails);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user_id = useSelector((state) => state.user.user_id);
  const role = useSelector((state) => state.user.role);

  const getProfile = async () => {
    try {
      if (role === "student") {
        const res = await getStudentApplication(user_id);
        dispatch(setUserDetails(res.data));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getCounselorTitle = () => {
    if (
      location.pathname.includes("sop") ||
      location.pathname.includes("lor") ||
      location.pathname.includes("cv_upload") ||
      location.pathname.includes("university_specific_sop") ||
      location.pathname.includes("final_documents_upload")
    ) {
      return "Assigned Content Writer";
    } else if (location.pathname.includes("shortlisting_of_universities")) {
      return "Assigned Counselor";
    }
    return "Counselor";
  };

  const isTokenExpired = () => {
    const expirationTime = localStorage.getItem("expires");
    if (!expirationTime) return true; // No expiration time set, treat as expired
    const currentTime = new Date().getTime();
    return currentTime > expirationTime;
  };

  const getStudentProfile = async (user_id) => {
    try {
      const res = await getStudentDetails(user_id);
      dispatch(setUserDetails(res.data));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    // Check if token is expired
    if (isTokenExpired()) {
      localStorage.removeItem("token");
      navigate("/login"); // Redirect to login page if token is expired
    } else {
      getStudentProfile(user_id);
    }
  }, [dispatch, navigate, user_id]);

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/status");
    } else if (location.pathname === "/documents_upload") {
      navigate("/documents_upload/cv_upload");
    }
    getProfile();
  }, [location.pathname, navigate]);

  // Determine which counselor to display based on the current page
  const isShortlistingPage = location.pathname.includes(
    "shortlisting_of_universities"
  );
  const assignedCounselorId = isShortlistingPage
    ? studentDetails?.assigned_admins?.assigned_to?.id
    : studentDetails?.assigned_admins?.assigned_content_writer?.id;

  // Counselor details based on current page
  const counselorDetails = isShortlistingPage
    ? studentDetails?.assigned_admins?.assigned_to
    : studentDetails?.assigned_admins?.assigned_content_writer;

  const counselorName = counselorDetails?.username || "N/A";
  const counselorPhone = counselorDetails?.phone || "N/A";
  const counselorEmail = counselorDetails?.email || "N/A";
  const counselorAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    counselorName === "N/A" ? "Unknown User" : counselorName
  )}&background=random&bold=true`;

  // const showComments =
  //   !location.pathname.includes("ug_only") && !isTableFullScreen;

  return (
    // <div
    //   className={`${
    //     isTableFullScreen
    //       ? "fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
    //       : "p-4"
    //   } flex  transition-all duration-300 ease-in-out
    //   ${location.pathname.includes("ug_only") ? "" : "flex-wrap "}`}
    // >
    <div
      className={`${
        isTableFullScreen
          ? "fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
          : "p-4"
      } flex  justify-center  transition-all duration-300 ease-in-out flex-wrap`}
    >
      <div
        className={`flex flex-col ${
          isTableFullScreen
            ? "w-full h-full"
            : // : location.pathname.includes("ug_only")
              // ? "w-1/2 border-r-2 border-indigo-500"
              "w-full max-w-screen-xl"
        } gap-5`}
      >
        <div className="flex flex-wrap justify-center items-start gap-5 transition-all duration-300 ease-in-out">
          {!isTableFullScreen && (
            <>
              {location.pathname.includes("ug_only") && (
                <div className="grow w-full">
                  <div className="bg-white p-6 rounded-lg shadow-md border border-blue-200 transition-all duration-300 ease-in-out w-full">
                    <Outlet />
                  </div>
                </div>
              )}
              {!location.pathname.includes("ug_only") && (
                <div className="grow shrink">
                  <div className=" bg-white p-6 rounded-lg shadow-md border border-blue-200 transition-all duration-300 ease-in-out">
                    <Outlet />
                  </div>
                </div>
              )}
            </>
          )}

          {!isTableFullScreen && (
            <div className="shrink-0 flex flex-1 md:flex-none flex-col md:flex-row xl:flex-col flex-wrap gap-5 justify-center transition-all duration-300 ease-in-out">
              <div className="relative w-[300px] flex flex-col gap-4">
                {/* Instructions Box */}
                {!location.pathname.includes("ug_only") && (
                  <>
                    <div className="w-full bg-white rounded-lg shadow-md border border-green-200">
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-3 flex items-center">
                        <IconFileText className="text-white mr-2" size={18} />
                        <h3 className="text-white font-semibold">
                          Instructions
                        </h3>
                      </div>
                      <div className="p-0">
                        <ContentBox
                          type="instructions"
                          className="max-h-[300px] overflow-y-auto p-4"
                        />
                      </div>
                    </div>

                    {/* Resources Box */}
                    <div className="w-full bg-white rounded-lg shadow-md border border-blue-200">
                      <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-3 flex items-center">
                        <IconBook className="text-white mr-2" size={18} />
                        <h3 className="text-white font-semibold">Resources</h3>
                      </div>
                      <div className="p-0">
                        <ContentBox
                          type="resources"
                          className="max-h-[300px] overflow-y-auto p-4"
                        />
                      </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-md border border-purple-100 overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-500 to-indigo-500 px-4 py-3 flex items-center">
                        <IconSchool className="text-white mr-2" size={18} />
                        <h3 className="text-white font-semibold">
                          {getCounselorTitle()}
                        </h3>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center mb-4">
                          <div className="relative">
                            <img
                              src={counselorAvatar}
                              alt="Counselor"
                              className="w-12 h-12 rounded-full border-2 border-purple-100"
                            />
                            {/* <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div> */}
                          </div>
                          <div className="ml-3">
                            <p className="font-semibold text-gray-800">
                              {counselorName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {getCounselorTitle()}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center text-gray-700">
                            <IconPhone
                              size={16}
                              className="mr-2 text-gray-500"
                              stroke={1.5}
                            />
                            <span>{counselorPhone}</span>
                          </div>
                          <div className="flex items-center text-gray-700">
                            <IconMail
                              size={16}
                              className="mr-2 text-gray-500"
                              stroke={1.5}
                            />
                            <span className="truncate">{counselorEmail}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Counselor Info Box */}
              </div>
            </div>
          )}
        </div>

        {/* Shortlisted Universities Table - CENTERED IN FULLSCREEN */}
        {studentDetails.shortlisting &&
          location.pathname.includes("shortlisting_of_universities") && (
            <div
              className={`relative ${
                isTableFullScreen
                  ? "w-full h-full mx-auto rounded-xl"
                  : "w-full h-[700px] md:h-[750px]"
              } bg-white shadow-md border border-blue-200 overflow-hidden`}
            >
              <ShortlistedTable activeTab="shortlisting" />
            </div>
          )}
      </div>

      {/* Comments Section */}
      {/* {showComments && ( */}
      <>
        {/* {!location.pathname.includes("ug_only") && (
          <> */}
        <hr className="border-t-2 border-indigo-500 w-full my-5" />
        {/* </>
        )} */}
        <Comments assignedCounselorId={assignedCounselorId} />
      </>
      {/* )} */}
    </div>
  );
};

export default StudentApplicationProcess;
